import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import CatchingOnLogo from '@/media/images/CatchinOn_Nav.png';

export const Navbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    const navLinks = [
        { name: 'HOME', path: '/' },
        { name: 'ABOUT', path: '/about' },
        { name: 'SERVICES', path: '/services' },
        { name: 'BLOG', path: '/blog' },
        { name: 'CONTACT', path: '/contact' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <header
            className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-brand-lime py-2 overflow-hidden"
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <nav className="flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" data-testid="logo-link">
                        <img src={CatchingOnLogo} alt="Catching On" className="h-20 w-auto" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-10">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`font-bold text-sm tracking-wide transition-colors duration-200 ${
                                    isActive(link.path)
                                        ? 'text-black'
                                        : 'text-black/70 hover:text-black'
                                }`}
                                data-testid={`nav-${link.name.toLowerCase()}`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {isMobileMenuOpen ? (
                            <X className="w-6 h-6 text-black" />
                        ) : (
                            <Menu className="w-6 h-6 text-black" />
                        )}
                    </button>
                </nav>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-brand-lime border-t-2 border-black"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`font-bold text-lg ${
                                        isActive(link.path)
                                            ? 'text-black'
                                            : 'text-black/70'
                                    }`}
                                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
